import type { DeleteObligationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC delete response to match the GraphQL mutation structure.
 *
 * The GraphQL `deleteObligation` mutation reported two affected-row counts
 * (`delete_obligation_impact` + `delete_obligation`). The tRPC backend is a
 * single bulk delete returning one affected-count for the obligation rows;
 * the impact cascade happens server-side. We surface a single deleted
 * obligation (the wrapper exposes a single-Id API) and a zero impact count,
 * matching the legacy shape consumers branch on (`delete_obligation`'s
 * `affected_rows === 1`).
 */
const mapTrpcResponseToGraphQL = (): DeleteObligationMutation => {
  return {
    delete_obligation_impact: {
      __typename: 'obligation_impact_mutation_response',
      affected_rows: 0,
    },
    delete_obligation: {
      __typename: 'obligation_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useDeleteObligationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligation.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.obligationsByType.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.obligationList.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `obligation_impact`.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationImpact.pathKey(),
        });
      },
    }),
  });

  return {
    deleteObligation: async (id: string): Promise<DeleteObligationMutation> => {
      await mutation.mutateAsync({ Ids: [id] });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
